import { useState } from 'react';

import { keepPreviousData, useQuery } from '@tanstack/react-query';
import { statusMailPreviewResponse } from 'cpool-schemas';
import type { StatusMailPreviewRequest } from 'cpool-schemas';

import { APICommunicator, APIError } from '~/api/APICommunicator';
import { queryKeys } from '~/utils/queryKeys';

const fetchStatusMailPreview = async (leadId: string, request: StatusMailPreviewRequest) => {
    const route = `/messages/preview/status-mail?aggregateId=${leadId}`;
    // shouldThrow: false — a thrown 401/403 would redirect to /accessdenied and kick the user out of the lead
    const post = () =>
        APICommunicator.apiFetch({
            context: { body: JSON.stringify(request), method: 'POST' },
            route,
            shouldThrow: false,
        });

    let response = await post();
    if (response.status === 401) {
        // renew() returns a raw Response while another renew is running, so check truthiness only
        const isJWTRenewed = await APICommunicator.renew();
        if (isJWTRenewed) response = await post();
    }
    if (!response.ok) throw new APIError({ info: await response.text(), status: response.status, url: route });

    return statusMailPreviewResponse.parse(await response.json());
};

export const useStatusMailPreview = ({
    enabled,
    leadId,
    request,
}: {
    enabled: boolean;
    leadId: string;
    request: StatusMailPreviewRequest | null;
}) => {
    const isPreviewEnabled = enabled && !!request;
    const selectionKey = JSON.stringify([leadId, request, isPreviewEnabled]);
    // the previous preview only bridges two previewable selections: after one without preview (no template,
    // nothing chosen) TanStack would bring back the mail of an older selection as placeholder
    const [selection, setSelection] = useState({ followsPreview: false, isPreviewEnabled, key: selectionKey });
    if (selection.key !== selectionKey) {
        setSelection({ followsPreview: selection.isPreviewEnabled, isPreviewEnabled, key: selectionKey });
    }

    return useQuery({
        enabled: isPreviewEnabled,
        placeholderData: selection.followsPreview ? keepPreviousData : undefined,
        queryFn: () => fetchStatusMailPreview(leadId, request as StatusMailPreviewRequest),
        queryKey: queryKeys.getStatusMailPreview(leadId, request),
        retry: false,
        staleTime: 0,
    });
};
